import React, { useState } from 'react';
import {
  dayOfMonth,
  formatHours,
  formatLongDate,
  isWeekend,
  secondsToHours,
  weekdayShort,
} from '../../utils/timesheet';
import { TaskCell, TaskPicker } from './TimesheetParts';

// Heat map shading: how many hours a cell needs for each level.
const shadeFor = (seconds) => (seconds >= 6 * 3600 ? 3 : seconds >= 3 * 3600 ? 2 : seconds > 0 ? 1 : 0);

/**
 * The pay period as a heat map: a row per task, a column per day, each cell
 * shaded by its hours and each day's total under its heading. Read-mostly by
 * design - an entry needs a description, which a number in a cell cannot
 * carry - so a click opens the entry instead:
 *
 *   empty cell     log time for that task and day
 *   one entry      edit it
 *   several        open that day in the day view, where they are listed
 */
export default function PeriodGrid({
  days,
  today,
  rowTaskIds,
  extraTaskIds,
  taskById,
  cellMap,
  tasks,
  editable,
  onCellClick,
  onAddRow,
  onRemoveRow,
  onCopyPrevious,
  copyingPrevious = false,
  onDayClick,
  // date -> { severity, count }: the days a validation check flags.
  dayFlags = null,
}) {
  const [adding, setAdding] = useState(null);

  const dayTotals = days.map((d) => rowTaskIds.reduce((sum, taskId) => (
    sum + (cellMap.get(`${taskId}|${d}`) || []).reduce((s, e) => s + e.durationSeconds, 0)
  ), 0));
  const grand = dayTotals.reduce((a, b) => a + b, 0);

  return (
    <div className="nsi-table-wrap">
      <table className="nsi-table ts-grid">
        <thead>
          <tr>
            <th className="ts-grid__task">Project / task</th>
            {days.map((d) => {
              const flag = dayFlags?.get(d);
              const flagText = flag ? ` - ${flag.count} check${flag.count === 1 ? '' : 's'} flagged` : '';
              return (
                <th
                  key={d}
                  className={`ts-grid__day${isWeekend(d) ? ' is-weekend' : ''}${d === today ? ' is-today' : ''}`}
                  title={`${formatLongDate(d)}${flagText}`}
                >
                  <button type="button" className="ts-grid__dayhead" onClick={() => onDayClick(d)} aria-label={`Open ${formatLongDate(d)}${flagText}`}>
                    <span className="ts-grid__dow">{weekdayShort(d)}</span>
                    <span className="ts-grid__dom">{dayOfMonth(d)}</span>
                    <span className="ts-grid__daytotal">{dayTotals[days.indexOf(d)] ? formatHours(secondsToHours(dayTotals[days.indexOf(d)])) : '·'}</span>
                    {flag ? <i className={`bi bi-exclamation-triangle-fill ts-flag ts-flag--${flag.severity} ts-grid__flag`} aria-hidden="true" /> : null}
                  </button>
                </th>
              );
            })}
            <th className="ts-grid__total nsi-table__right">
              Total
              <span className="ts-grid__daytotal">{formatHours(secondsToHours(grand))}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rowTaskIds.length === 0 ? (
            <tr>
              <td className="nsi-table__empty" colSpan={days.length + 2}>
                Nothing logged in this pay period yet.
                {editable ? ' Add a row below, or start the timer.' : ''}
              </td>
            </tr>
          ) : rowTaskIds.map((taskId) => {
            const task = taskById.get(taskId);
            const root = task ? taskById.get(task.rootId) : null;
            let rowSeconds = 0;
            const isExtra = extraTaskIds.includes(taskId);
            return (
              <tr key={taskId}>
                <td className="ts-grid__task">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <TaskCell task={task} color={root?.color} />
                    {isExtra && editable ? (
                      <button type="button" className="bdc-linkbtn" onClick={() => onRemoveRow(taskId)} aria-label="Remove this empty row" title="Remove row">
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </td>
                {days.map((d) => {
                  const cell = cellMap.get(`${taskId}|${d}`) || [];
                  const seconds = cell.reduce((s, e) => s + e.durationSeconds, 0);
                  rowSeconds += seconds;
                  const locked = cell.some((e) => e.locked);
                  const filled = cell.length > 0;
                  const canAct = filled || editable;
                  const label = filled
                    ? `${formatHours(secondsToHours(seconds))} hours on ${formatLongDate(d)}${cell.length > 1 ? `, ${cell.length} entries` : ''}`
                    : `Log time on ${formatLongDate(d)}`;
                  return (
                    <td key={d} className={`ts-grid__day${isWeekend(d) ? ' is-weekend' : ''}`}>
                      <button
                        type="button"
                        className={[
                          'ts-cell',
                          filled ? `ts-cell--filled ts-cell--l${shadeFor(seconds)}` : 'ts-cell--empty',
                          cell.length > 1 ? 'ts-cell--multi' : '',
                          locked ? 'ts-cell--locked' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => onCellClick(taskId, d, cell)}
                        disabled={!canAct}
                        aria-label={label}
                        title={filled ? cell.map((e) => e.description).filter(Boolean).join('\n') || label : label}
                      >
                        {filled ? formatHours(secondsToHours(seconds)) : <span className="ts-cell__add" aria-hidden="true">+</span>}
                      </button>
                    </td>
                  );
                })}
                <td className="ts-grid__total nsi-table__right bdc-num">{formatHours(secondsToHours(rowSeconds))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {editable ? (
        <div className="ts-addrow">
          <div className="ts-addrow__picker">
            <TaskPicker
              tasks={tasks}
              value={adding}
              onChange={(value) => {
                if (value) onAddRow(value);
                setAdding(null);
              }}
              placeholder="Add a row for a project / task…"
              disabledItemValues={rowTaskIds}
            />
          </div>
          <button type="button" className="bdc-btn bdc-btn--sm" onClick={onCopyPrevious} disabled={copyingPrevious}>
            <i className="bi bi-files" aria-hidden="true" />
            Copy rows from last period
          </button>
        </div>
      ) : null}
    </div>
  );
}
